import { FC, ReactNode, MouseEvent } from 'react'
import clsx from 'clsx'
import { Button, OverlayTrigger, Spinner, Tooltip } from 'react-bootstrap'
import SectionHeaderInfoTooltip from './SectionHeaderInfoTooltip'

const aiAutoMapTooltipStyle = {
  fontSize: '12px',
  minWidth: '280px',
  maxWidth: '420px',
  whiteSpace: 'pre-line' as const,
  textAlign: 'left' as const,
}

export const ProfileSectionAiAutoMapButton: FC<{
  onClick?: (e: MouseEvent<HTMLButtonElement>) => void
  disabled?: boolean
  loading?: boolean
  helpTooltip?: string
  helpTooltipId?: string
}> = ({
  onClick,
  disabled = false,
  loading = false,
  helpTooltip,
  helpTooltipId = 'listing-profile-ai-auto-map-tooltip',
}) => {
  const button = (
    <Button
      type="button"
      size="sm"
      variant="primary"
      className="product-listing__toolbar-btn product-listing__ai-auto-map-btn fw-semibold"
      disabled={disabled || loading}
      onClick={(e) => {
        e.stopPropagation()
        onClick?.(e)
      }}
    >
      {loading ? (
        <>
          <Spinner animation="border" size="sm" variant="light" role="status">
            <span className="visually-hidden">Mapping</span>
          </Spinner>
          <span className="ms-1">Mapping…</span>
        </>
      ) : (
        <>
          <span aria-hidden="true">✨</span> AI Auto-Map
        </>
      )}
    </Button>
  )

  return (
    <span
      className="product-listing__ai-auto-map-control d-inline-flex align-items-center gap-1"
      onClick={(e) => e.stopPropagation()}
    >
      {helpTooltip ? (
        <OverlayTrigger
          placement="top"
          overlay={
            <Tooltip id={`${helpTooltipId}-button`} style={aiAutoMapTooltipStyle}>
              {helpTooltip}
            </Tooltip>
          }
        >
          <span className="d-inline-flex">{button}</span>
        </OverlayTrigger>
      ) : (
        button
      )}
      {helpTooltip ? (
        <SectionHeaderInfoTooltip id={helpTooltipId} message={helpTooltip} />
      ) : null}
    </span>
  )
}

export interface ListingProfileSectionAccordionProps {
  sectionId: string
  letter: string
  isExpanded: boolean
  onToggle: () => void
  title: ReactNode
  summaryLine?: ReactNode
  headerTrailing?: ReactNode
  highlight?: boolean
  /** Save/validation error — highlights card so user can find the section */
  validationHighlight?: boolean
  /** Create flow: section depends on target channel — header click shows guidance instead of expanding */
  toggleDisabled?: boolean
  onToggleBlocked?: () => void
  children: ReactNode
  showNextButton?: boolean
  onNext?: () => void
  showPreviousButton?: boolean
  onPrevious?: () => void
  footerLeading?: ReactNode
}

const ListingProfileSectionAccordion: FC<ListingProfileSectionAccordionProps> = ({
  sectionId,
  letter,
  isExpanded,
  onToggle,
  title,
  summaryLine,
  headerTrailing,
  highlight = false,
  validationHighlight = false,
  toggleDisabled = false,
  onToggleBlocked,
  children,
  showNextButton = false,
  onNext,
  showPreviousButton = false,
  onPrevious,
  footerLeading,
}) => {
  const showFooterNav =
    (showPreviousButton && onPrevious) || (showNextButton && onNext)
  /** Keep panel mounted whenever the accordion renders so sections hydrate while collapsed. */
  return (
  <section
    id={sectionId}
    className={clsx(
      'product-listing__profile-accordion',
      highlight && 'product-listing__profile-accordion--highlight',
      validationHighlight && 'product-listing__profile-accordion--validation-error',
      toggleDisabled && 'product-listing__profile-accordion--locked',
      isExpanded && 'product-listing__profile-accordion--expanded'
    )}
  >
    <button
      type="button"
      className="product-listing__profile-accordion-header"
      onClick={() => {
        if (toggleDisabled) {
          onToggleBlocked?.()
          return
        }
        onToggle()
      }}
      aria-expanded={toggleDisabled ? false : isExpanded}
      aria-disabled={toggleDisabled || undefined}
      aria-controls={`${sectionId}-panel`}
    >
      <div className="product-listing__profile-accordion-header-main">
        <div className="product-listing__profile-accordion-letter" aria-hidden="true">
          {letter}
        </div>
        <div className="product-listing__profile-accordion-titles">
          <div className="product-listing__profile-accordion-title">{title}</div>
          {summaryLine ? (
            <p className="product-listing__profile-accordion-summary">{summaryLine}</p>
          ) : null}
        </div>
      </div>
      <div className="product-listing__profile-accordion-header-actions">
        {headerTrailing}
        <svg
          className={clsx(
            'product-listing__profile-accordion-chevron',
            !isExpanded && 'product-listing__profile-accordion-chevron--collapsed'
          )}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
          aria-hidden="true"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </div>
    </button>
    <div
      id={`${sectionId}-panel`}
      className={clsx(
        'product-listing__profile-accordion-panel',
        !isExpanded && 'product-listing__profile-accordion-panel--collapsed'
      )}
      hidden={!isExpanded || toggleDisabled}
      aria-hidden={!isExpanded || toggleDisabled}
    >
      <div className="product-listing__profile-accordion-body">{children}</div>
      {footerLeading || showFooterNav ? (
        <div
          className={clsx(
            'product-listing__profile-accordion-footer',
            footerLeading && showFooterNav && 'product-listing__profile-accordion-footer--split'
          )}
        >
          {footerLeading ? <div className="product-listing__profile-accordion-footer-start">{footerLeading}</div> : null}
          {showFooterNav ? (
            <div className="product-listing__profile-accordion-footer-nav d-flex align-items-center gap-2 flex-shrink-0">
              {showPreviousButton && onPrevious ? (
                <Button
                  type="button"
                  size="sm"
                  variant="outline-primary"
                  className="product-listing__toolbar-btn fw-semibold"
                  onClick={onPrevious}
                >
                  ← Previous
                </Button>
              ) : null}
              {showNextButton && onNext ? (
                <Button
                  type="button"
                  size="sm"
                  variant="primary"
                  className="product-listing__toolbar-btn fw-semibold"
                  onClick={onNext}
                >
                  Next →
                </Button>
              ) : null}
            </div>
          ) : null}
        </div>
      ) : null}
    </div>
  </section>
  )
}

export default ListingProfileSectionAccordion
